import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { DateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { OverrideModule } from '@mandants/telekom/override/override.module';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { UserRoleAssignmentRow } from '../user-permissions.component';

export interface ExtendUserRoleDialogData {
  row: UserRoleAssignmentRow;
}

@Component({
  selector: 'app-extend-user-role',
  templateUrl: './extend-user-role.component.html',
  styleUrls: [],
  imports: [
    CommonModule,
    SmartusComponentsModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    MatFormFieldModule,
    MatDialogModule,
    OverrideModule,
  ],
})
export class ExtendUserRoleComponent implements OnInit {
  form = new FormGroup({});

  formModel: any = {};

  formFields: FormlyFieldConfig[] = [
    {
      key: 'employeeInformation',
      type: 'input',
      wrappers: ['readOnly'],
      props: {
        label: this.translateService.instant(
          'access_orderer_representative.dialog.employee'
        ),
        readOnly: true,
        valueFormatter: () => {
          return [this.data.row.username, this.data.row.email]
            .filter(Boolean)
            .join(', ');
        },
      },
    },
    {
      key: 'roleName',
      type: 'input',
      wrappers: ['readOnly'],
      props: {
        label: this.translateService.instant(
          'user.request.permissions.assignment.table.role'
        ),
        readOnly: true,
      },
    },
    {
      key: 'validUntil',
      type: 'input',
      wrappers: ['readOnly'],
      props: {
        label: this.translateService.instant(
          'user.request.permissions.assignment.table.validUntil'
        ),
        readOnly: true,
        valueFormatter: (validUntil: Date) => {
          if (!validUntil) {
            return '--';
          }
          return moment(validUntil)
            .locale(this.translateService.instant('locale.locale'))
            .format(
              this.translateService.instant('locale.moment__date_format')
            );
        },
      },
    },
    {
      key: 'extendDate',
      type: 'datepicker',
      // Vorbelegt mit einem Jahr ueber die bisherige Gueltigkeit hinaus.
      defaultValue: this.defaultExtendDate(),
      props: {
        label: this.translateService.instant(
          'user.request.permissions.assignment.extend_dialog.label__extendUntil'
        ),
        required: true,
        datepickerOptions: {
          // Eine Verlaengerung kann nicht vor dem bisherigen Ende liegen.
          min: this.currentValidUntil(),
          filter: (date: unknown) => this.isSelectableExtendDate(date),
        },
      },
      validators: {
        minDate: {
          expression: (control: AbstractControl) =>
            this.isSelectableExtendDate(control.value),
          message: () =>
            this.translateService.instant(
              'user.request.permissions.assignment.extend_dialog.invalid_date'
            ),
        },
      },
    },
  ];

  constructor(
    private translateService: TranslateService,
    // Derselbe Adapter, den der Datepicker verwendet. Er laeuft laut
    // material-config.ts mit useUtc, deshalb darf hier nicht mit lokalen
    // Date-Objekten gerechnet werden: lokale Mitternacht ist in UTC noch der
    // Vortag und das Minimum landet einen Tag zu frueh.
    private dateAdapter: DateAdapter<unknown>,
    public dialogRef: MatDialogRef<ExtendUserRoleComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ExtendUserRoleDialogData
  ) {}

  ngOnInit() {
    this.formModel = {
      ...this.data.row,
      extendDate: this.defaultExtendDate(),
    };
  }

  validate() {
    if (this.form.valid) {
      this.dialogRef.close(this.toNativeDate(this.form.value['extendDate']));
    } else {
      this.form.markAllAsTouched();
    }
  }

  /**
   * Wert als Datum des Adapters, oder null wenn er nicht auswertbar ist. Ein
   * Date wird ueber seine Kalenderbestandteile uebernommen und nicht ueber
   * deserialize: das rechnet den Zeitpunkt nach UTC um, wodurch lokale
   * Mitternacht auf dem Vortag landen wuerde.
   */
  private toDate(value: unknown): unknown | null {
    if (value instanceof Date) {
      return this.dateAdapter.createDate(
        value.getFullYear(),
        value.getMonth(),
        value.getDate()
      );
    }

    return this.dateAdapter.getValidDateOrNull(
      this.dateAdapter.deserialize(value)
    );
  }

  /** Das im Kalender angezeigte Datum als lokales Date. */
  private toNativeDate(value: unknown): Date | undefined {
    const date = this.toDate(value);

    if (!date) {
      return undefined;
    }

    return new Date(
      this.dateAdapter.getYear(date),
      this.dateAdapter.getMonth(date),
      this.dateAdapter.getDate(date)
    );
  }

  /**
   * Bisheriges Ende der Zuweisung, oder heute wenn keines hinterlegt ist. Bei
   * einer bereits abgelaufenen Zuweisung zaehlt ebenfalls heute, da die
   * Verlaengerung erst ab heute gilt.
   */
  private currentValidUntil(): unknown {
    const today = this.dateAdapter.today();
    const validUntil = this.toDate(this.data.row?.validUntil);

    if (!validUntil || this.dateAdapter.compareDate(validUntil, today) < 0) {
      return today;
    }

    return validUntil;
  }

  /** Vorschlag fuer die Verlaengerung: ein Jahr ueber das bisherige Ende. */
  private defaultExtendDate(): unknown {
    return this.dateAdapter.addCalendarYears(this.currentValidUntil(), 1);
  }

  private isSelectableExtendDate(value: unknown): boolean {
    const date = this.toDate(value);

    // Leere Tage im Kalenderraster reicht Material ebenfalls durch, ein leeres
    // Feld faengt required ab.
    if (!date) {
      return true;
    }

    return this.dateAdapter.compareDate(date, this.currentValidUntil()) >= 0;
  }
}
